// ════════════════════════════════════════════════════════════════════════
//  Fassungen des Lehrgangs
// ════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseShape, type Bundle } from "@otto/lehrgang";
import type { Json, OttoClient } from "@otto/plattform";
import { CURRICULA_KEY } from "../../curricula/lib/data.js";

/**
 * Nach jedem Vorgang an einer Fassung sind ZWEI Abfragen alt.
 *
 * Die Fassungen dieses Lehrgangs – klar. Aber auch die Lehrgangsliste: sie
 * führt je Zeile den Stand („Entwurf offen“, „Fassung 13 veröffentlicht“), und
 * genau der ändert sich hier. Vorher blieb die Liste bis zu einer Minute stehen
 * und behauptete den alten Stand; aufgefallen ist es an der Begleitleiste, die
 * ihre eine gemessene Station aus derselben Abfrage speist und den frisch
 * angelegten Entwurf nicht bemerkte.
 */
function invalidateVersions(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: STORAGE_KEY });
  void queryClient.invalidateQueries({ queryKey: CURRICULA_KEY });
}

export type Version = {
  readonly id: string;
  readonly curriculumId: string;
  readonly version: number;
  readonly notes: string | null;
  readonly publishedAt: string | null;
  readonly content: unknown;
  /** Wer sie angelegt hat. Null bei Zeilen von vor der Spalte. */
  readonly createdBy: string | null;
};

const STORAGE_KEY = ["editorial", "versions"] as const;

export const versionsKeyOf = (slug: string) => [...STORAGE_KEY, slug] as const;

/**
 * Die Fassungen EINES Lehrgangs.
 *
 * Der Filter ist nicht Kosmetik. Ohne ihn sah diese Abfrage die Fassungen ALLER
 * Lehrgänge, nahm die höchste veröffentlichte als Vorlage für einen neuen
 * Entwurf und berechnete die nächste Fassungsnummer aus fremder Zählung.
 * Aufgefallen ist das, weil die Testsuite in einem eigenen Lehrgang schreibt und
 * die Redaktion sich deren „Testfassung“ als Vorlage nahm. Damals stand hier ein
 * fester Slug; jetzt steht er in der Adresse.
 */
export function useVersions(client: OttoClient, slug: string) {
  return useQuery({
    queryKey: versionsKeyOf(slug),
    enabled: slug !== "",
    queryFn: async (): Promise<Version[]> => {
      const { data, error } = await client
        .from("curriculum_versions")
        .select(
          "id, curriculum_id, version, notes, published_at, content, created_by, curricula!inner(slug)",
        )
        .eq("curricula.slug", slug)
        .order("version", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((z) => ({
        id: z.id,
        curriculumId: z.curriculum_id,
        version: z.version,
        notes: z.notes,
        publishedAt: z.published_at,
        createdBy: z.created_by,
        content: z.content,
      }));
    },
  });
}

/**
 * Das Bundle einer Fassung – nur auf FORM geprüft.
 *
 * Absichtlich nicht `parseBundle`: das prüft zusätzlich den Inhalt, und ein
 * Entwurf, an dem noch gearbeitet wird, hat regelmäßig Hinweise. Mit der
 * strengen Prüfung ließ er sich nach dem Speichern nicht mehr LADEN – die
 * Arbeitsfläche fiel auf „noch keine Fassung“ zurück, und der Entwurf war nur
 * noch über „Verwerfen“ erreichbar. Bei einem frisch angelegten, leeren
 * Lehrgang passierte das sofort.
 *
 * Die Hinweise gehen dabei nicht verloren: `validate` zeigt sie neben dem Feld,
 * und `publishGate` sperrt das Veröffentlichen, solange welche offen sind.
 */
export function bundleOf(version: Version) {
  return parseShape(version.content);
}

/**
 * Legt einen Entwurf an – als Kopie der neuesten veröffentlichten Fassung, nicht
 * leer. Eine Redaktion ändert Wortmaterial, sie fängt nicht von vorn an.
 *
 * Gibt es noch gar keine Fassung, dient das eingebackene Bundle als Start.
 */
export function useCreateDraft(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      curriculumId: string;
      template: Bundle;
      nextVersion: number;
    }) => {
      const { data, error } = await client
        .from("curriculum_versions")
        .insert({
          curriculum_id: input.curriculumId,
          version: input.nextVersion,
          // Das Bundle ist gültiges JSON; der generierte Json-Typ kennt nur
          // seine eigene Struktur, deshalb hier einmal umdeuten.
          content: input.template as unknown as Json,
          notes: "",
        })
        .select("id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateVersions(queryClient),
  });
}

export function useSaveDraft(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; bundle: Bundle; notes: string }) => {
      const { error } = await client
        .from("curriculum_versions")
        .update({
          content: input.bundle as unknown as Json,
          notes: input.notes,
        })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => invalidateVersions(queryClient),
  });
}

export function useDiscardDraft(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await client.from("curriculum_versions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidateVersions(queryClient),
  });
}

export const VERSIONS_KEY = STORAGE_KEY;
